 
import { FullClient } from 'domains/client';
import { Contact } from 'domains/contact/types';
import { Cpt, formatCptCode } from 'domains/cpt';
import { ContractedBusiness } from 'domains/insurance';
import { AnyNote } from 'domains/notes/types';
import { Therapist } from 'domains/therapists';
import { ServiceLineForDisplay, SuperbillDisplayMode } from '../ui';
import { getContactAddressLine2 } from './addresses';

interface CalculateSelfPayProps {
  therapist: Therapist;
  cptData: Cpt | undefined;
  paidByPatient: number;
  client: FullClient;
  contact: Contact;
  note: AnyNote;
  contractedBusiness: ContractedBusiness;
  charge: number;
}

export const calculateSelfPayProps = ({
  cptData,
  therapist,
  contractedBusiness,
  paidByPatient,
  client,
  contact,
  note,
  charge,
}: CalculateSelfPayProps) => {
  const serviceLines: ServiceLineForDisplay[] = [
    {
      // @note(diogo): `serviceLineID` is used as the `key` special React property
      // when rendering the services table. For insurance, it's generated by
      // Candid. For self-pay, the value is generated here. The only
      // requirement is that it must be unique among the items of the array.
      serviceLineID: '1',

      cptCode: cptData && formatCptCode(cptData),
      cptDescription: cptData && cptData.description,
      charge,
      allowed: 0,
      paidByPatient,
    },
  ];

  const totalCharges = serviceLines.reduce((total, x) => total + x.charge, 0);
  const totalPaidByPatient = serviceLines.reduce((total, x) => total + x.paidByPatient, 0);

  const contactAddress = note.contactAddress ?? client.primaryContact?.address ?? contact.address;

  return {
    mode: SuperbillDisplayMode.SelfPay,
    renderingProviderName: `${therapist.firstName} ${therapist.lastName}`,
    renderingProviderLicenseNumber: therapist.licenseNumbers,
    renderingProviderNpi: therapist.npi,
    billingProviderName: contractedBusiness.billingProvider.organization_name,
    billingProviderTaxID: contractedBusiness.billingProvider.tax_id,
    billingProviderNpi: contractedBusiness.billingProvider.npi,
    billingAddressLines: [
      contractedBusiness.billingProvider.address.address1,
      contractedBusiness.billingProvider.address.address2,
      contractedBusiness.billingProvider.address && getContactAddressLine2(contractedBusiness.billingProvider.address),
    ].filter(Boolean) as string[],
    serviceAddressLines: [
      contractedBusiness.serviceFacility.address.address1,
      contractedBusiness.serviceFacility.address.address2,
      contractedBusiness.serviceFacility.address && getContactAddressLine2(contractedBusiness.serviceFacility.address),
    ].filter(Boolean) as string[],
    patientName: `${client.clientFirstName} ${client.clientLastName}`,
    patientDateOfBirth: note.clientDateOfBirth ?? client.clientDateOfBirth,
    patientAddressLines: [
      contactAddress?.street,
      contactAddress?.line2,
      contactAddress && getContactAddressLine2(contactAddress),
    ].filter(Boolean) as string[],
    serviceLines,
    totalCharges,
    totalAllowed: 0,
    totalPaidByPatient,
    balanceDue: totalCharges - totalPaidByPatient,
    dateOfService: note.appointmentOn,
    therapistName: `${therapist.firstName} ${therapist.lastName}, CCC-SLP`,
  };
};
